import { difference, keys } from 'lodash';

export const PUSH_ACTION_TYPE = '@@router/CALL_HISTORY_METHOD';

/**
** Method to retrieve hitcount changes and build an object
** that will be used to update the appropriate Jira ticket.
 * @param {*} metric The metric object to parse
 * @param {*} type CCF or NCF
 * @param {*} art A single artifact that is being investigated.\
*  ! All other artifact hitcount differences will be ignored if this is set.
 * @param {*} delta The difference between the total hitcount baseline \
 * and the artifact that is being investigated's hitcount difference.
 */
export const getHitCountChanges = (metric, type, art, delta) => {
  const hitCounts = metric[type].artHitCounts;
  const currentKeys = Object.keys(hitCounts.current);
  const baselineKeysNotInCurrent = difference(
    Object.keys(hitCounts.baseline),
    currentKeys
  );
  const differencesToRevert = {};

  //accounts for hitcount changes to artifacts that are in both current and baseline
  const differences = currentKeys.reduce((result, currentKey) => {
    const currentHitCount = hitCounts.current[currentKey];
    const baselineHitCount = hitCounts.baseline[currentKey] ?
      hitCounts.baseline[currentKey] : 0;
    const difference = currentHitCount - baselineHitCount;

    if (difference !== 0) {
      switch (art) {
        case currentKey:
        case undefined:
          result.push(`* ${difference > 0 ? '+' : ''}${difference} ${currentKey}`);
          break;
        default:
          differencesToRevert[currentKey] = hitCounts.baseline[currentKey];
          break;
      }
    }

    return result;
  }, []);

  //accounts for hitcount changes for artifacts that are not present in current
  const changes = art && differences[0] && differences[0].includes(art) ? [] :
    baselineKeysNotInCurrent.reduce((result, key) => {
      if ((art && key === art) || art === undefined) {
        result.push(`* -${hitCounts.baseline[key]} ${key}`);
      }
      return result;
    }, []);

  //base the return value off of the current hitcounts
  const toReturn = Object.keys(hitCounts.current).sort().reduce((result, key) => {
    result[key] = hitCounts.current[key];
    return result;
  }, {});

  // if a single artifact hitcount is being updated and other hitcounts were
  // not present in the current due to not being produced by the build, add their baseline
  // back so as to not update them inadvertently.
  if (art) {
    baselineKeysNotInCurrent.forEach(key => {
      if (key !== art) {
        toReturn[key] = hitCounts.baseline[key];
      }
    });

    keys(differencesToRevert).forEach(k => {
      toReturn[k] = differencesToRevert[k];
    });

  }

  //modified return values must be copied to this object as
  //outside method depends on this specific object name
  const newValue = Object.keys(toReturn).sort().reduce((result, key) => {
    result[key] = toReturn[key];
    return result;
  }, {});
  const returnDelta = delta ?
    {
      diff: delta,
      new: metric[type].hitCountMetrics.baseline + delta,
      old: metric[type].hitCountMetrics.baseline
    } :
    {
      diff: metric[type].hitCountMetrics.diff,
      new: metric[type].hitCountMetrics.current,
      old: metric[type].hitCountMetrics.baseline
    };

  return {
    newValue,
    changes: changes.concat(differences),
    type,
    delta: returnDelta
  };
};

export const getDurationChanges = (metric, type) => {
  const baseline = !isNaN(metric.durationMetrics.baseline) ?
    metric.durationMetrics.baseline : 0;

  const difference = baseline ?
    metric.durationMetrics.current - metric.durationMetrics.baseline :
    metric.durationMetrics.current;

  const comment =
    `* ${difference > 0 ? '+' : ''}${difference} ${type.toUpperCase()} DURATION AVERAGE`;
  const changes = difference !== 0 ? [comment] : [];
  const newValue = metric.durationMetrics.current;
  return {
    newValue,
    changes: changes,
    type: type.toLowerCase(),
    delta: {
      diff: difference,
      new: metric.durationMetrics.current,
      old: baseline
    }
  };
};

/**
** Method to retrieve sourcecount changes and build an object
** that will be used to update the appropriate Jira ticket.
* @param metric The metric object to parse for changes.
*/
export const getSourceCountChanges = (metric) => {
  const baseline = !isNaN(metric.sourceCountMetrics.baseline) ?
    metric.sourceCountMetrics.baseline : 0;

  const difference = baseline ?
    metric.sourceCountMetrics.current - metric.sourceCountMetrics.baseline :
    metric.sourceCountMetrics.current;

  const changes = difference !== 0 ?
    [`* ${difference > 0 ? '+' : ''}${difference} SOURCE COUNTS`] : [];
  const newValue = metric.sourceCountMetrics.current;
  return {
    newValue,
    changes: changes,
    type: 'ncf',
    delta: {
      diff: difference,
      new: metric.sourceCountMetrics.current,
      old: baseline
    }
  };
};

/**
** Method to retrieve hitcount changes and build an object
** that will be used to update the appropriate Jira ticket.
 * @param {*} metric The metric object to parse.
 */
export const getSourceAttributeCountChanges = (metric) => {
  const baseline = !isNaN(metric.sourceAttributeCountMetrics.baseline) ?
    metric.sourceAttributeCountMetrics.baseline : 0;

  const difference = baseline ?
    metric.sourceAttributeCountMetrics.current -
    metric.sourceAttributeCountMetrics.baseline :
    metric.sourceAttributeCountMetrics.current;

  const changes = difference !== 0 ?
    [`* ${difference > 0 ? '+' : ''}${difference} SOURCE ATTRIBUTE COUNTS`] : [];
  const newValue = metric.sourceAttributeCountMetrics.current;

  return {
    newValue,
    changes: changes,
    type: 'ncf',
    delta: {
      diff: difference,
      new: metric.sourceAttributeCountMetrics.current,
      old: baseline
    }
  };
};


// WEBPACK FOOTER //
// src/helpers/actions.js